import { useState } from 'react';
import { Plus, Filter, TrendingUp, DollarSign, AlertCircle, CreditCard as Edit2, Trash2, Calendar, X, Calculator } from 'lucide-react';
import { useInvestments, Investment } from '../hooks/useInvestments';
import { useInvestmentStats } from '../hooks/useInvestmentCashflow';
import { FinancingPlanModal } from '../components/investments/FinancingPlanModal';

export default function InvestmentsListPage() {
  const { investments, loading, deleteInvestment, createInvestment, updateInvestment } = useInvestments();
  const { stats, loading: statsLoading } = useInvestmentStats();
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterYear, setFilterYear] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<Investment | null>(null);
  const [showFinancingPlan, setShowFinancingPlan] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    category: 'Machines atelier' as Investment['category'],
    description: '',
    total_budget: '',
    planned_year: '2024-2025',
    planned_start_date: '',
    priority: 'Should' as Investment['priority'],
    status: 'Planned' as Investment['status'],
    supplier: '',
    notes: ''
  });

  const filteredInvestments = investments.filter(inv => {
    if (filterCategory !== 'all' && inv.category !== filterCategory) return false;
    if (filterYear !== 'all' && inv.planned_year !== filterYear) return false;
    if (filterStatus !== 'all' && inv.status !== filterStatus) return false;
    return true;
  });

  const handleDelete = async (id: string) => {
    try {
      await deleteInvestment(id);
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting investment:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const openEditModal = (investment: Investment) => {
    setFormData({
      name: investment.name,
      category: investment.category,
      description: investment.description || '',
      total_budget: investment.total_budget.toString(),
      planned_year: investment.planned_year || '2024-2025',
      planned_start_date: investment.planned_start_date || '',
      priority: investment.priority,
      status: investment.status,
      supplier: investment.supplier || '',
      notes: investment.notes || ''
    });
    setShowEditModal(investment);
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createInvestment({
        name: formData.name,
        category: formData.category,
        description: formData.description || undefined,
        total_budget: parseFloat(formData.total_budget),
        planned_year: formData.planned_year || undefined,
        planned_start_date: formData.planned_start_date || undefined,
        priority: formData.priority,
        status: formData.status,
        supplier: formData.supplier || undefined,
        notes: formData.notes || undefined
      });
      setShowCreateModal(false);
      setFormData({
        name: '',
        category: 'Machines atelier',
        description: '',
        total_budget: '',
        planned_year: '2024-2025',
        planned_start_date: '',
        priority: 'Should',
        status: 'Planned',
        supplier: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating investment:', error);
      alert('Erreur lors de la création');
    }
  };

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showEditModal) return;

    try {
      await updateInvestment(showEditModal.id, {
        name: formData.name,
        category: formData.category,
        description: formData.description || undefined,
        total_budget: parseFloat(formData.total_budget),
        planned_year: formData.planned_year || undefined,
        planned_start_date: formData.planned_start_date || undefined,
        priority: formData.priority,
        status: formData.status,
        supplier: formData.supplier || undefined,
        notes: formData.notes || undefined
      });
      setShowEditModal(null);
      setFormData({
        name: '',
        category: 'Machines atelier',
        description: '',
        total_budget: '',
        planned_year: '2024-2025',
        planned_start_date: '',
        priority: 'Should',
        status: 'Planned',
        supplier: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error updating investment:', error);
      alert('Erreur lors de la modification');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Planned': return 'bg-gray-100 text-gray-700';
      case 'In progress': return 'bg-blue-100 text-blue-700';
      case 'Done': return 'bg-green-100 text-green-700';
      case 'Cancelled': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'Must': return 'text-red-600 font-semibold';
      case 'Should': return 'text-orange-600 font-medium';
      case 'Could': return 'text-gray-600';
      default: return 'text-gray-600';
    }
  };

  if (loading || statsLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="text-gray-500">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">Plan d'Investissement</h2>
          <p className="text-sm text-gray-600 mt-1">
            Gérez vos investissements pluriannuels et leurs financements
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowFinancingPlan(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
          >
            <Calculator size={16} />
            Financements
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
          >
            <Plus size={18} />
            Nouvel investissement
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-blue-600" size={20} />
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-600">Total Investissements</div>
              <div className="text-xl font-semibold text-gray-900">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.totalInvestments)}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-green-600" size={20} />
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-600">Financements Attendus</div>
              <div className="text-xl font-semibold text-gray-900">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.totalFundings)}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <AlertCircle className="text-orange-600" size={20} />
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-600">Reste à Financer</div>
              <div className="text-xl font-semibold text-gray-900">
                {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(stats.remainingToFund)}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <Filter className="text-red-600" size={20} />
            </div>
            <div className="flex-1">
              <div className="text-xs text-gray-600">Sans Financement</div>
              <div className="text-xl font-semibold text-gray-900">{stats.unfundedCount}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Catégorie</label>
              <select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">Toutes</option>
                <option value="Machines atelier">Machines atelier</option>
                <option value="Outillages">Outillages</option>
                <option value="Informatique">Informatique</option>
                <option value="Mobilier scolaire">Mobilier scolaire</option>
                <option value="Travaux bâtiment">Travaux bâtiment</option>
                <option value="Autre">Autre</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Année</label>
              <select
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">Toutes</option>
                <option value="2024-2025">2024-2025</option>
                <option value="2025-2026">2025-2026</option>
                <option value="2026-2027">2026-2027</option>
              </select>
            </div>

            <div>
              <label className="block text-xs text-gray-600 mb-1">Statut</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm"
              >
                <option value="all">Tous</option>
                <option value="Planned">Planifié</option>
                <option value="In progress">En cours</option>
                <option value="Done">Terminé</option>
                <option value="Cancelled">Annulé</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Investissement</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-700">Catégorie</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700">Budget</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700">Décaissés prévus</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700">Financé</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-700">Reste à financer</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700">Priorité</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700">Statut</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredInvestments.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                    Aucun investissement trouvé
                  </td>
                </tr>
              ) : (
                filteredInvestments.map(inv => (
                  <tr
                    key={inv.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{inv.name}</div>
                      {inv.planned_year && (
                        <div className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                          <Calendar size={12} />
                          {inv.planned_year}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{inv.category}</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(inv.total_budget)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 text-right">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(inv.disbursements_total)}
                    </td>
                    <td className="px-4 py-3 text-sm text-green-600 font-medium text-right">
                      {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(inv.funded_amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right">
                      <span className={inv.remaining_to_fund > 0 ? 'text-orange-600 font-medium' : 'text-gray-400'}>
                        {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.max(0, inv.remaining_to_fund))}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`text-xs ${getPriorityColor(inv.priority)}`}>
                        {inv.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(inv.status)}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEditModal(inv)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(inv.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">{showEditModal ? 'Modifier l\'investissement' : 'Nouvel investissement'}</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(null);
                }}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={showEditModal ? handleEdit : handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value as Investment['category'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Machines atelier">Machines atelier</option>
                    <option value="Outillages">Outillages</option>
                    <option value="Informatique">Informatique</option>
                    <option value="Mobilier scolaire">Mobilier scolaire</option>
                    <option value="Travaux bâtiment">Travaux bâtiment</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Budget total (€) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.total_budget}
                    onChange={(e) => setFormData({ ...formData, total_budget: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année prévue</label>
                  <select
                    value={formData.planned_year}
                    onChange={(e) => setFormData({ ...formData, planned_year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="2024-2025">2024-2025</option>
                    <option value="2025-2026">2025-2026</option>
                    <option value="2026-2027">2026-2027</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                  <input
                    type="date"
                    value={formData.planned_start_date}
                    onChange={(e) => setFormData({ ...formData, planned_start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priorité</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value as Investment['priority'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Must">Must</option>
                    <option value="Should">Should</option>
                    <option value="Could">Could</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as Investment['status'] })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="Planned">Planifié</option>
                    <option value="In progress">En cours</option>
                    <option value="Done">Terminé</option>
                    <option value="Cancelled">Annulé</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                <input
                  type="text"
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  rows={2}
                />
              </div>
              <div className="flex gap-3 justify-end pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {showEditModal ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
            <p className="text-gray-600 mb-6">
              Êtes-vous sûr de vouloir supprimer cet investissement ? Cette action supprimera également tous les décaissements et allocations associés.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {showFinancingPlan && (
        <FinancingPlanModal
          investments={investments}
          onClose={() => setShowFinancingPlan(false)}
        />
      )}
    </div>
  );
}
